/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../common';
import { aiSystemApi } from '../../services/api';
import { AISystemFormAdvanced } from './AISystemFormAdvanced';
import PageBackButton from '../../../components/PageBackButton';

const AISystemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: system, isLoading, error, refetch } = useQuery({
    queryKey: ['ai-system', id],
    queryFn: () => aiSystemApi.get(Number(id)),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always fetch fresh data
  });

  const handleClose = () => {
    // Navigate back to systems list
    navigate('/ai/systems');
  };

  const systemForView = useMemo(() => {
    if (!system) {
      return system;
    }

    if (searchParams.get('mode') !== 'ai') {
      return system;
    }

    return {
      ...system,
      config: {
        ...(system.config || {}),
        initial_builder_mode: 'ai',
      },
    };
  }, [searchParams, system]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !system) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="AI System Not Found"
          backButton={<PageBackButton label="Back to Systems" to="/ai/systems" preferHistory={false} hideLabelOnMobile />}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.message || 'System not found'}
        </Alert>
      </Box>
    );
  }

  const handleSystemUpdate = async () => {
    await refetch();
  };

  return (
    <AISystemFormAdvanced
      key={`system-form-${system?.id}-${searchParams.get('mode') || 'manual'}`}
      open={true}
      onClose={handleClose}
      system={systemForView}
      onSystemUpdate={handleSystemUpdate}
      asPage={true}
    />
  );
};

export default AISystemDetail;
